import { config } from "../config/config";
import { getSdk } from "../services/sdkService";
import { i18n } from "../services/i18nService";
import { showSingleModalButton } from "../services/modalsService";
import { getErrorCode } from "./codes";
import { getGlobalButtonInfo } from "./globalButtons";

/**
 * Builds and throws a 422 error for when the user is trying to make a signup request by sending
 * a non valid email.
 * @param {String} userSent - user string sent in signup request.
 */
export function getSignupInvalidEmailError(userSent) {
  const validationError = new Error();
  validationError.status = 422;
  validationError.message = `Invalid user email: ${userSent}`;
  throw validationError;
}

/**
 * Calls function to show global modal by passing it a config object.
 * It sends an error report if it's an error to be reported. It requires the context of a React
 * component wrapped in appContext to work (as it uses this.props.stateActions).
 * @param {Object} config global modal config object to configure the buttons and callbacks
 * @param {Object} errorData Defines if it's an error to be reported.
 */
export function showAndReportUserRelatedError(config, errorData) {
  this.props.stateActions.showGlobalModal(config);
  errorData && sendUserNotLoggedErrorReport(errorData);
}

/**
 *
 */
export function getLoginErrorModalConfig({
  isRetrying = false,
  retryImplementation = null,
  cancelImplementation = null,
  isFromSignup = false
}) {
  let buttons;
  if (!!isRetrying) {
    const { stateActions } = this.props;
    const retry = () => {
      stateActions.hideGlobalModal();
      retryImplementation();
    };
    const cancel = () => {
      stateActions.hideGlobalModal();
      cancelImplementation();
    };
    buttons = [
      {
        ...getGlobalButtonInfo("retry"),
        buttonHandler: retry
      },
      {
        ...getGlobalButtonInfo("cancel"),
        buttonHandler: cancel
      }
    ];
  }
  return {
    modalTitle: !isFromSignup && i18n.get("pages.login.errors.couldNotLogin.title"),
    modalText: isFromSignup
      ? i18n.get("pages.signup.errors.notLogin")
      : i18n.get("pages.login.errors.couldNotLogin.text"),
    buttons: buttons || showSingleModalButton("close", this.closeModal)
  };
}

/**
 * Returns a global modal config object to be used when the internet connectivity is lost or returns a timeout. Given that this modal needs
 * to appear in several places in the app, this common function serves as the single point from where all those places
 * can get the configuration object. Each caller can specify a custom retry implementation, which will be bound to the
 * retry button's action.
 * @param {Function} retryImplementation - Function with a custom implementation for the retry button of the modal.
 * @param {Function} backImplementation - Function with a custom implementation for the back button of the modal.
 * @param {Array} customButtons - Custom buttons to appear in the modal.
 * @param {Boolean} needsDismissableProp - Boolean that defines whether the modal will be dismissable or not.
 * @param {Boolean} isTimeout - Boolean that defines if the error is related to a timeout to show a timeout related message instead of the
 * simple disconnection one.
 * @return {Object} the modal configuration object with title, message and 2 buttons.
 */
export function getInternetConnectivityModalConfig({
  retryImplementation,
  backImplementation,
  customButtons,
  needsDismissableProp = true,
  isTimeout = false
}) {
  const buttons = customButtons || getDisconnectedButtons.call(this, retryImplementation, backImplementation);
  const modalText = isTimeout
    ? i18n.get("global.errors.timeout.text")
    : i18n.get("global.errors.internetConnectivity.text");

  return {
    ...(!!needsDismissableProp && { dismissable: false }),
    modalTitle: i18n.get("global.errors.internetConnectivity.title"),
    modalText,
    buttons,
    onShowListener: () => getSdk().systemManager.network.on("networkConnected", retryImplementation),
    onHideListener: () => getSdk().systemManager.network.off("networkConnected", retryImplementation)
  };
}

function getDisconnectedButtons(retryImplementation, backImplementation) {
  const { isRoot = false } = this.props || {};
  const buttons = [
    {
      ...getGlobalButtonInfo("retry"),
      buttonHandler: retryImplementation
    }
  ];
  if (config.get().featureToggles.exitAppPopup && isRoot) {
    buttons.push({
      ...getGlobalButtonInfo("exit"),
      buttonHandler: getSdk().systemManager.application.exit
    });
  } else {
    buttons.push({
      ...getGlobalButtonInfo("back"),
      buttonHandler: backImplementation
    });
  }
  return buttons;
}

/**
 * Sends a user-not-logged error related report.
 */
async function sendUserNotLoggedErrorReport(errorData) {
  const sdk = await getSdk();
  const code = getErrorCode(errorData.errorName);
  sdk.reportsManager.sendReport(
    { level: errorData.status, event: errorData.event, env: config.get().sdk.environment },
    { eventId: "PlayerError", report: code, status: errorData.status, parameter: { errorCode: code } }
  );
}
